import { useState, useMemo, useEffect, useRef } from "react";
import {
  doc,
  writeBatch,
  deleteField,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from "@firebase/firestore";
import useMembersData from "../useMembersData";
import usePackages from "../../../../../hooks/usePackages";
import useAdminDataStore, {
  selectCurrentGroupData,
} from "../../../../../store/adminData/adminData";
import { db } from "../../../../../services/firebaseService/connection";
import { fetchMembers } from "../../../../../services/api/members";
import { assignGroupMemberNumberIfNeeded } from "../../../../../services/firebaseService/endPoints/admin/members";
import logger from "../../../../../utils/logger";
import { getMinPackageSeasonFromTimeline } from "../../../../../helperFunctions/util";

const useSyncPenyaNumbers = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { membersList, membersLoading } = useMembersData();
  const { activePackages, packagesBySeason, currentPackages } = usePackages();

  const [showInitialWarning, setShowInitialWarning] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingNewMembers, setSyncingNewMembers] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const hasAssignedNewNumbersRef = useRef(false);

  const resolvePackageSeason = (member) => {
    if (member.packageSeason) return member.packageSeason;
    const pkgDetails = currentPackages?.find(
      (pkg) => pkg?.id === member.package,
    );
    return pkgDetails
      ? getMinPackageSeasonFromTimeline(pkgDetails?.packageTimeline)
      : null;
  };

  const getPackageName = (member) => {
    if (!member.package) return null;
    const packageSeason = resolvePackageSeason(member);
    if (!packageSeason) return null;
    const pkgKey = `${member.package}|${packageSeason}`;
    return packagesBySeason?.find((p) => p.key === pkgKey)?.value || null;
  };

  const isMemberActive = (member) => {
    if (member.lockStatus) return false;
    if (member.userRole === "fan") return false;
    if (!member.package) return false;

    const packageSeason = resolvePackageSeason(member);
    if (!packageSeason) return false;

    return !!activePackages?.find(
      (pkg) => pkg.key === `${member.package}|${packageSeason}`,
    );
  };

  // All members with a groupMemberNumber, annotated with their new number and change type
  const allMembersWithNumbers = useMemo(() => {
    if (!membersList) return [];

    const membersWithNumber = membersList
      .filter(
        (m) =>
          m.groupMemberNumber != null &&
          m.groupMemberNumber !== "" &&
          !isNaN(Number(m.groupMemberNumber)),
      )
      .sort(
        (a, b) => Number(a.groupMemberNumber) - Number(b.groupMemberNumber),
      );

    const active = membersWithNumber.filter((m) => isMemberActive(m));
    const inactive = membersWithNumber.filter((m) => !isMemberActive(m));

    const activeAnnotated = active.map((member, index) => {
      const newNumber = index + 1;
      const changed = Number(member.groupMemberNumber) !== newNumber;
      return {
        id: member.id,
        email: member.email,
        name: member.name,
        packageName: getPackageName(member),
        autoRenew: member?.subscriptionDetail?.status === "active",
        oldNumber: Number(member.groupMemberNumber),
        newNumber,
        type: changed ? "renumber" : "unchanged",
      };
    });

    const inactiveAnnotated = inactive.map((member) => ({
      id: member.id,
      email: member.email,
      name: member.name,
      packageName: getPackageName(member),
      autoRenew: member?.subscriptionDetail?.status === "active",
      oldNumber: Number(member.groupMemberNumber),
      newNumber: null,
      type: "remove",
    }));

    return [...activeAnnotated, ...inactiveAnnotated].sort(
      (a, b) => a.oldNumber - b.oldNumber,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersList, activePackages, packagesBySeason]);

  // How every member in the Members tab relates to numbering. Buckets are
  // mutually exclusive and sum to membersList.length.
  const memberBreakdown = useMemo(() => {
    if (!membersList) return null;
    const hasNum = (m) =>
      m.groupMemberNumber != null &&
      m.groupMemberNumber !== "" &&
      !isNaN(Number(m.groupMemberNumber));
    const b = {
      activeNumbered: 0,
      activeNoNumber: 0,
      lapsedNumbered: 0,
      lapsedNoNumber: 0,
      locked: 0,
      fans: 0,
      noPackage: 0,
    };
    membersList.forEach((m) => {
      if (isMemberActive(m)) {
        if (hasNum(m)) b.activeNumbered += 1;
        else b.activeNoNumber += 1;
      } else if (hasNum(m)) b.lapsedNumbered += 1;
      else if (m.package) b.lapsedNoNumber += 1;
      else if (m.lockStatus) b.locked += 1;
      else if (m.userRole === "fan") b.fans += 1;
      else b.noPackage += 1;
    });
    return { ...b, total: membersList.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersList, activePackages]);

  const syncChanges = useMemo(
    () => allMembersWithNumbers.filter((m) => m.type !== "unchanged"),
    [allMembersWithNumbers],
  );

  // Assign a groupMemberNumber to any active member who joined/renewed without
  // one, as soon as the modal opens, so the renumber preview below reflects
  // them too instead of silently skipping them.
  useEffect(() => {
    if (
      !isModalOpen ||
      membersLoading ||
      !membersList ||
      hasAssignedNewNumbersRef.current
    ) {
      return;
    }
    hasAssignedNewNumbersRef.current = true;

    // Includes invite-only rows (negative numeric id): they are numbered on
    // their invite_emails doc via email.
    const numberlessActiveMembers = membersList
      .filter(
        (m) =>
          (typeof m.id === "string" ||
            (typeof m.id === "number" && m.id < 0)) &&
          (m.groupMemberNumber == null || m.groupMemberNumber === "") &&
          isMemberActive(m),
      )
      // Oldest joiners get the next number first, so numbering still reflects
      // join order rather than fetch order.
      .sort(
        (a, b) =>
          Number(a.registeredOnChantDate || 0) -
          Number(b.registeredOnChantDate || 0),
      );

    if (numberlessActiveMembers.length === 0) return;

    setSyncingNewMembers(true);
    (async () => {
      for (const member of numberlessActiveMembers) {
        await assignGroupMemberNumberIfNeeded(
          groupData.id,
          typeof member.id === "string" ? member.id : null,
          member.email,
        ).catch((err) =>
          logger.error("Error assigning group member number:", err),
        );
      }
      await fetchMembers(groupData.id);
      setSyncingNewMembers(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, membersLoading, membersList]);

  const handleOpen = () => {
    setShowInitialWarning(true);
  };

  const handleContinueFromWarning = () => {
    setShowInitialWarning(false);
    setShowConfirm(false);
    hasAssignedNewNumbersRef.current = false;
    fetchMembers(groupData.id);
    setIsModalOpen(true);
  };

  const handleCloseWarning = () => {
    setShowInitialWarning(false);
  };

  const handleClose = () => {
    if (syncing || syncingNewMembers) return;
    setIsModalOpen(false);
    setShowConfirm(false);
  };

  const handleSync = async () => {
    if (!groupData?.id || syncChanges.length === 0) return;

    try {
      setSyncing(true);
      const BATCH_LIMIT = 450; // Firestore caps a batch at 500 writes
      const ops = [];

      // userGroup members have string IDs, invite_email members have negative number IDs
      const userGroupChanges = syncChanges.filter(
        ({ id }) => typeof id === "string",
      );
      const inviteEmailChanges = syncChanges.filter(
        ({ id }) => typeof id === "number" && id < 0,
      );

      const valueFor = (newNumber) =>
        newNumber === null ? deleteField() : String(newNumber);

      userGroupChanges.forEach(({ id, newNumber }) => {
        ops.push({
          ref: doc(db, "userGroup", groupData.id, "members", String(id)),
          value: valueFor(newNumber),
        });
      });

      // Update invite_email members by querying their email
      for (const { email, newNumber } of inviteEmailChanges) {
        if (!email) continue;
        const q = query(
          collection(db, "invite_emails", groupData.id, "userDetails"),
          where("email", "==", email),
        );
        const snap = await getDocs(q);
        snap.docs.forEach((docSnap) => {
          ops.push({ ref: docSnap.ref, value: valueFor(newNumber) });
        });
      }

      for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        ops
          .slice(i, i + BATCH_LIMIT)
          .forEach(({ ref, value }) =>
            batch.update(ref, { groupMemberNumber: value }),
          );
        await batch.commit();
      }

      // Keep the live counter ahead of the highest number now in use, so the
      // next new member cannot collide with a renumbered one.
      const maxNumber = allMembersWithNumbers.reduce(
        (max, m) =>
          m.newNumber != null && m.newNumber > max ? m.newNumber : max,
        0,
      );
      await updateDoc(doc(db, "group", groupData.id), {
        nextGroupMemberNumber: maxNumber + 1,
      });

      await fetchMembers(groupData.id);
      handleClose();
    } catch (err) {
      logger.error("Error syncing group member numbers:", err);
    } finally {
      setSyncing(false);
    }
  };

  return {
    showInitialWarning,
    handleContinueFromWarning,
    handleCloseWarning,
    isModalOpen,
    handleOpen,
    handleClose,
    allMembersWithNumbers,
    memberBreakdown,
    syncChanges,
    syncing,
    syncingNewMembers,
    showConfirm,
    setShowConfirm,
    handleSync,
  };
};

export default useSyncPenyaNumbers;
